/**
 * Copyright (c) 2026, CheckPlus Limited
 */
import React, { Component } from "react";
import { renderErrorMsg } from "../../../assets/component/renderErrorMsg.js";
import ToggleSwitch from "../../../assets/component/ToggleSwitch/ToggleSwitch.js";
import { mapPaymentMethodDataStatus, mapPaymentMethodData } from "../../../assets/helper.js";
import PropTypes from "prop-types";
import UpgradeNoticeCard from "./UpgradeNoticeCard";

const paymentMethods = [
  {
    key: "bank_transfer",
    label: "Bank Transfer",
  }
];

const defaultFormData = {
  bankTransferCode: '',
  bankTransferBranchCode: '',
  bankTransferAccountName: '',
  bankTransferAccountNumber: '',
};

class RenderBankTransfer extends Component {
  constructor(props) {
    super(props);
    this.state = {
      isEditMode: false,
      formData: defaultFormData,
      isOtherBank: false,
    };

    this.handleToggleEditMode = this.handleToggleEditMode.bind(this);
    this.handleInputChange = this.handleInputChange.bind(this);
    this.handleBankSelectChange = this.handleBankSelectChange.bind(this);
    this.handleSave = this.handleSave.bind(this);
    this.mapFormDataFromProps = this.mapFormDataFromProps.bind(this);
    this.mapFieldText = this.mapFieldText.bind(this);
    this.renderErrorMsg = this.renderErrorMsg.bind(this);
  }

  componentDidMount() {
    const newFormData = this.mapFormDataFromProps();

    // Check if the saved bank code is in the list, if not set isOtherBank
    const bankList = this.getBankList();
    const savedBankCode = newFormData.bankTransferCode;
    const isInList = bankList.some(bank => bank[1] === savedBankCode);
    const isOtherBank = savedBankCode && !isInList;

    this.setState({
      formData: newFormData,
      isOtherBank,
    });
  }

  handleToggleEditMode() {
    const { isEditMode } = this.state;

    const newState = !isEditMode;

    if (newState === false) {
      // reset form data when cancel edit
      const newFormData = this.mapFormDataFromProps();
      this.setState({
        formData: newFormData,
      });
    }

    this.setState({
      isEditMode: newState,
    });
  }

  handleInputChange(e) {
    const { name, value } = e.target;
    this.setState((prevState) => ({
      formData: {
        ...prevState.formData,
        [name]: value,
      },
    }));
  }

  handleBankSelectChange(e) {
    const { value } = e.target;

    if (value === "OTHER") {
      // User selected "Other" - show text input
      this.setState((prevState) => ({
        isOtherBank: true,
        formData: {
          ...prevState.formData,
          bankTransferCode: '',
        },
      }));
    } else {
      // User selected a bank from the list
      this.setState((prevState) => ({
        isOtherBank: false,
        formData: {
          ...prevState.formData,
          bankTransferCode: value,
        },
      }));
    }
  }

  getBankList() {
    const { languageObj } = this.props;
    // Get bank list from languageObj, fallback to default bank list
    const bankList = languageObj?.OrgBankAdd?.bankList;

    if (bankList && bankList.length > 0) {
      // Add "Other" option if not present
      const hasOther = bankList.some(bank => bank[1] === "OTHER");
      if (!hasOther) {
        return [...bankList, ["Other (Enter Bank Code)", "OTHER"]];
      }
      return bankList;
    }

    // Fallback bank list for Hong Kong banks
    return [
      ["Select a Bank", ""],
      ["HSBC Bank (004)", "004"],
      ["Hang Seng Bank Limited (024)", "024"],
      ["Standard Chartered Bank (003)", "003"],
      ["Bank of China (Hong Kong) (012)", "012"],
      ["Bank of East Asia (015)", "015"],
      ["DBS Bank (Hong Kong) (016)", "016"],
      ["Citibank (N.A.) (006)", "006"],
      ["China Construction Bank (009)", "009"],
      ["Industrial and Commercial Bank of China (072)", "072"],
      ["Nanyang Commercial Bank (043)", "043"],
      ["OCBC Wing Hang Bank (035)", "035"],
      ["Shanghai Commercial Bank (025)", "025"],
      ["Public Bank (Hong Kong) (028)", "028"],
      ["Chiyu Banking Corporation (039)", "039"],
      ["Dah Sing Bank (040)", "040"],
      ["Fubon Bank (Hong Kong) (128)", "128"],
      ["China CITIC Bank International (018)", "018"],
      ["CMB Wing Lung Bank (020)", "020"],
      ["Chong Hing Bank (041)", "041"],
      ["Other (Enter Bank Code)", "OTHER"]
    ];
  }

  getBankNameFromCode(code) {
    if (!code) {
      return '-';
    }
    const bankList = this.getBankList();
    const bank = bankList.find(b => b[1] === code);
    return bank ? bank[0] : code;
  }

  handleSave() {
    const { formData } = this.state;
    const { func } = this.props;

    func.handleUpdatePaymentData(paymentMethods[0].key, paymentMethods[0].label, formData);

    this.setState({ isEditMode: false });
  }

  mapFormDataFromProps() {
    const { state } = this.props;
    const { formData } = this.state;

    let newFormData = formData;

    const paymentCodes = state.organizationObj.paymentMethodCode;

    if (paymentCodes) {
      const getPaymentData = paymentCodes[paymentMethods[0].key] || null;

      if (getPaymentData) {
        // reset to latest data from props
        newFormData = {
          bankTransferCode: getPaymentData.bankTransferCode || '',
          bankTransferBranchCode: getPaymentData.bankTransferBranchCode || '',
          bankTransferAccountName: getPaymentData.bankTransferAccountName || '',
          bankTransferAccountNumber: getPaymentData.bankTransferAccountNumber || '',
        };
      } else {
        // reset to default
        newFormData = defaultFormData;
      }
    }

    return newFormData;
  }

  mapFieldText(field)  {
    const { formData } = this.state;

    let text = '-';

    if (formData[field] !== '') {
      text = formData[field];
    }

    return text;
  }

  renderErrorMsg() {
    const { state } = this.props;
    const errMsg = state.errMsgInfo.paymentCode;

    if (errMsg.method === paymentMethods[0].key && !errMsg.isCode) {
      return renderErrorMsg(errMsg.msg);
    }

    return null;
  }

  t(key, fallback) {
    const pmText = (this.props.languageObj && this.props.languageObj.PaymentMethods) || {};
    return pmText[key] || fallback;
  }

  render() {
    const { state, func } = this.props;
    const { isEditMode, formData, isOtherBank } = this.state;

    const banktransferStatus = mapPaymentMethodDataStatus(state.organizationObj.paymentMethodCode, paymentMethods[0].key);
    const banktransferData = mapPaymentMethodData(state.organizationObj.paymentMethodCode, paymentMethods[0].key);
    // Bank Transfer is configured if account number has data
    const banktransferIsConfigured = Boolean(banktransferData.bankTransferAccountNumber);

    if (state.loading) {
      return (
        <div className="RenderBankTransfer-loading">
          <div className="RenderBankTransfer-spinner"></div>
        </div>
      );
    }

    const currentPlan = state.organizationObj.currentPlan
      ? state.organizationObj.currentPlan.toLowerCase()
      : "";
    const isPremiumPlan = currentPlan === "premium";

    if (!isPremiumPlan) {
      return (
        <div className="RenderBankTransfer-container">
          <UpgradeNoticeCard onUpgradeClick={func.redirectToOrgBilling} />
        </div>
      );
    }

    return (
      <div className="RenderBankTransfer-container">
        <div className="RenderBankTransfer-content">
          <div className="RenderBankTransfer-toggleRow">
            <span className="RenderBankTransfer-toggleLabel">{this.t("enableBankTransfer", "Enable Bank Transfer")}</span>
            <ToggleSwitch
              checked={banktransferStatus.checked}
              onChange={(e) => func.handleUpdatePaymentStatus(e, paymentMethods[0].key)}
              disabled={!banktransferIsConfigured}
              disabledMessage={!banktransferIsConfigured ? this.t("fillRequiredFirst", "Please fill in the required fields first") : ""}
            />
          </div>

          <div className="RenderBankTransfer-formContainer">
            <div className="RenderBankTransfer-savedCredentials">

              <div className="RenderBankTransfer-group">
                <div className="RenderBankTransfer-subheading">{this.t("accountName", "Account Name")}</div>
                {isEditMode ? (
                  <div className="RenderBankTransfer-inputFieldContainer">
                    <input
                      type="text"
                      name="bankTransferAccountName"
                      className="RenderBankTransfer-inputField"
                      placeholder={this.t("accountName", "Account Name")}
                      value={formData.bankTransferAccountName}
                      onChange={this.handleInputChange}
                    />
                  </div>
                ) : (
                  <div className="RenderBankTransfer-textValue">
                    {this.mapFieldText('bankTransferAccountName')}
                  </div>
                )}
              </div>

              <div className="RenderBankTransfer-group">
                <div className="RenderBankTransfer-subheading">{this.t("bank", "Bank")}</div>
                {isEditMode ? (
                  <div className="RenderBankTransfer-inputFieldContainer">
                    <select
                      name="bankTransferCode"
                      className="RenderBankTransfer-inputField"
                      value={isOtherBank ? "OTHER" : formData.bankTransferCode}
                      onChange={this.handleBankSelectChange}
                    >
                      {this.getBankList().map((bank) => (
                        <option key={bank[1]} value={bank[1]}>
                          {bank[0]}
                        </option>
                      ))}
                    </select>
                    {isOtherBank && (
                      <input
                        type="text"
                        name="bankTransferCode"
                        className="RenderBankTransfer-inputField"
                        placeholder={this.t("enterBankCode", "Enter Bank Code (e.g., 004)")}
                        value={formData.bankTransferCode}
                        onChange={this.handleInputChange}
                        style={{ marginTop: '10px' }}
                      />
                    )}
                  </div>
                ) : (
                  <div className="RenderBankTransfer-textValue">
                    {this.getBankNameFromCode(formData.bankTransferCode)}
                  </div>
                )}
              </div>

              <div className="RenderBankTransfer-group">
                <div className="RenderBankTransfer-subheading">{this.t("branchCode", "Branch Code")}</div>
                {isEditMode ? (
                  <div className="RenderBankTransfer-inputFieldContainer">
                    <input
                      type="text"
                      name="bankTransferBranchCode"
                      className="RenderBankTransfer-inputField"
                      placeholder={this.t("branchCode", "Branch Code")}
                      value={formData.bankTransferBranchCode}
                      onChange={this.handleInputChange}
                    />
                  </div>
                ) : (
                  <div className="RenderBankTransfer-textValue">
                    {this.mapFieldText('bankTransferBranchCode')}
                  </div>
                )}
              </div>

              <div className="RenderBankTransfer-group">
                <div className="RenderBankTransfer-subheading">{this.t("accountNumber", "Account Number")}</div>
                {isEditMode ? (
                  <div className="RenderBankTransfer-inputFieldContainer">
                    <input
                      type="text"
                      name="bankTransferAccountNumber"
                      className="RenderBankTransfer-inputField"
                      placeholder={this.t("accountNumber", "Account Number")}
                      value={formData.bankTransferAccountNumber}
                      onChange={this.handleInputChange}
                    />
                  </div>
                ) : (
                  <div className="RenderBankTransfer-textValue">
                    {this.mapFieldText('bankTransferAccountNumber')}
                  </div>
                )}
              </div>

            </div>
          </div>

          {this.renderErrorMsg()}
        </div>
      </div>
    );
  }
}

RenderBankTransfer.propTypes = {
  state: PropTypes.object.isRequired,
  func: PropTypes.object.isRequired,
  languageObj: PropTypes.object,
};

export default RenderBankTransfer;
